import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@/lib/v2/components';

import { ETriggerActivityType } from '@/src/infrastructure/interfaces/IAutomation.interface';
import { useCampaignsService } from '@/src/infrastructure/Protocol/Campaigns/useCampaignsService';
import { useRulesService } from '@/src/infrastructure/Protocol/Rules/useRulesService';
import { BodyCreateRule } from '@/src/infrastructure/Protocol/ServiceInterfaces';
import { useService } from '@/src/infrastructure/Protocol/useEmblue';
import {
  AUTOMATION_PATHS,
  JOURNEY_BACK_URL_KEY,
} from '@/src/modules/AutomationModuleNew/constants';
import { useNavigateAndBack } from '@/src/modules/AutomationModuleNew/hooks/useNavigateAndBack';
import {
  JourneyMessageCreatedResponse,
  RuleCreatedResponse,
} from '@/src/modules/AutomationModuleNew/interfaces/automation';

import { FieldDTO, PayloadSaveRules, RuleTypes } from '@/modules/RulesModule/interfaces';

export const useSendEmail = () => {
  const { t } = useTranslation();
  const service = useService();
  const rulesService = useRulesService();
  const campaignsService = useCampaignsService();
  const [isLoading, setIsLoading] = useState(false);
  const { navigateAndSavePathname } = useNavigateAndBack(JOURNEY_BACK_URL_KEY);

  const createRule = useCallback(
    async (
      name: string
    ): Promise<
      | { isValidInput: true; data: RuleCreatedResponse | JourneyMessageCreatedResponse }
      | { isValidInput: false; message: string }
      | undefined
    > => {
      if (!service) return;

      const body: BodyCreateRule = {
        name,
        ruleType: ETriggerActivityType.API_INTEGRATOR,
      };
      const type = 'email_rules';
      const response = await service.createRule(body, type);
      if (response.success) {
        return { isValidInput: true, data: response.data as RuleCreatedResponse };
      } else if (response.data === 'RULE_ALREADY_EXISTS') {
        return {
          isValidInput: false,
          message: t(`useCreateRule.${type}.error.RULE_ALREADY_EXISTS`),
        };
      }

      toast({
        id: 'ruleCreated',
        title: t(`useCreateRule.${type}.error.title`),
        body: t(`useCreateRule.${type}.error.body`),
        variant: 'error',
      });
      setIsLoading(false);
      return {
        isValidInput: false,
        message: t(`useCreateRule.${type}.error.body`),
      };
    },
    [service, t]
  );

  const saveDefaultRuleTypeAfterCreate = useCallback(
    async (actionId: number) => {
      //cspell:disable
      const templatePayload = {
        acumuladaCantidad: null,
        campoDTO: {} as FieldDTO,
        camposPersonalizadosDTO: [],
        elementoId: actionId,
        elementosDTO: [],
        elementosHnosCantidad: 0,
        enviarCadaAnio: null,
        envioRecurrente: false,
        fechaEnvio: 'de inmediato',
        tag_id: [],
        tipo: 'Clicks',
        urlDestino: null,
        urlDestinoError: null,
      };
      //cspell:disable
      const payloadSave = {
        ...templatePayload,
        desencadenante: RuleTypes.API_INTEGRATOR,
      } as PayloadSaveRules;

      await rulesService.saveRule(payloadSave);
    },
    [rulesService]
  );

  const handleCreateRule = useCallback(
    async (name: string) => {
      setIsLoading(true);
      const response = await createRule(name);
      if (!response?.isValidInput) return response;

      const { id } = response.data;

      await saveDefaultRuleTypeAfterCreate(id);
      setIsLoading(false);
      return response;
    },
    [createRule, saveDefaultRuleTypeAfterCreate]
  );

  const getCampaignByActionId = useCallback(
    async (actionId: number) => {
      setIsLoading(true);
      const response = await campaignsService.getActionByID(actionId);
      if (response.action) {
        const { actionId, campaignId } = response.action;
        navigateAndSavePathname(
          `/v2/${AUTOMATION_PATHS.MODULE_PATH}/${AUTOMATION_PATHS.JOURNEYS_PATH}/${campaignId}/action/${actionId}/email-trigger/email`
        );
      }
      setIsLoading(false);
    },
    [campaignsService, navigateAndSavePathname]
  );

  return { isLoading, handleCreateRule, getCampaignByActionId };
};
